import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../context/AuthContext";
import axios from "axios";
import {
    Form,
    Card,
    Typography,
    Rate,
    Tag,
    Checkbox,
    Input,
    Radio,
    Button,
    Space,
    Divider,
    message,
    Spin,
    Alert,
} from "antd";
import {
    UserOutlined,
    CalendarOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;
const { TextArea } = Input;

const resignationReasonOptions = [
    { label: "Salary", value: "Salary" },
    { label: "Job Advancement", value: "Job Advancement" },
    { label: "Better Opportunity", value: "Better Opportunity" },
    { label: "Work-Life Balance", value: "Work-Life Balance" },
    { label: "Management", value: "Management" },
    { label: "Relocation", value: "Relocation" },
    { label: "Health Reasons", value: "Health Reasons" },
    { label: "Other", value: "Other" },
];

const timelineOptions = [
    { label: "Immediate", value: "Immediate" },
    { label: "1-2 weeks", value: "1-2_weeks" },
    { label: "1 month", value: "1_month" },
    { label: "1-2 months", value: "1-2_months" },
    { label: "More than 2 months", value: "More_than_2_months" },
];

export default function ExitInterview({employeeId}) {
    const { authData, backUrl } = useContext(AuthContext);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [employeeInfo, setEmployeeInfo] = useState(null);
    const [error, setError] = useState(null);

    const fetchMyInterview = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${backUrl}/api/payroll/exit-interview/my_interview/`, {
                headers: {
                    Authorization: `Bearer ${authData?.access}`,
                },
            });

            const data = res.data;
            setEmployeeInfo({
                employee_name: data.employee_name,
                designation: data.designation,
            });

            // Pre-fill form with existing data (if any)
            form.setFieldsValue({
                decision_timeline: data.decision_timeline,
                resignation_reasons: data.resignation_reasons || [],
                specific_event_triggered: data.specific_event_triggered,
                specific_event_details: data.specific_event_details,
                discussed_with_supervisor: data.discussed_with_supervisor,
                training_rating: data.training_rating,
                superior_relationship_rating: data.superior_relationship_rating,
                colleague_relationship_rating: data.colleague_relationship_rating,
                salary_rating: data.salary_rating,
                benefits_rating: data.benefits_rating,
                benefits_comments: data.benefits_comments,
                workload_rating: data.workload_rating,
                satisfaction_rating: data.satisfaction_rating,
                encountered_problems: data.encountered_problems,
                problems_comment: data.problems_comment,
                most_enjoyed: data.most_enjoyed,
                least_enjoyed: data.least_enjoyed,
                recommend_employer: data.recommend_employer,
                recommend_comment: data.recommend_comment,
                reapply_future: data.reapply_future,
                reapply_comment: data.reapply_comment,
                additional_comments: data.additional_comments,
            });
        } catch (err) {
            setError("Failed to load your exit interview form. Please try again.");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMyInterview();
    }, []);

    const onFinish = async (values) => {
        try {
            setSubmitting(true);
            await axios.patch(
                `${backUrl}/api/payroll/exit-interview/submit_answers/`,
                values,
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                    },
                }
            );
            alert("Exit interview submitted successfully!");
        } catch (err) {
            message.error("Failed to submit. Please try again.");
            console.error(err);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: "center", padding: "50px" }}>
                <Spin size="large" tip="Loading your exit interview form..." />
            </div>
        );
    }

    if (error) {
        return <Alert message="Error" description={error} type="error" showIcon />;
    }

    return (
        <div style={{ padding: "24px", maxWidth: "900px", margin: "0 auto" }}>
            <Card>
                <Title level={2}>Exit Interview Form</Title>
                {employeeInfo && (
                    <Space direction="vertical" style={{ marginBottom: 24 }}>
                        <Text strong>
                            <UserOutlined /> Employee: {employeeInfo.employee_name}
                        </Text>
                        <Text strong>Designation: {employeeInfo.designation}</Text>
                    </Space>
                )}

                <Divider />

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    autoComplete="off"
                >
                    {/* Resignation Reasons */}
                    <Form.Item
                        name="resignation_reasons"
                        label="Primary Reasons for Resignation (select all that apply)"
                        rules={[{ required: true, message: "Please select at least one reason" }]}
                    >
                        <Checkbox.Group options={resignationReasonOptions} />
                    </Form.Item>

                    {/* Specific Event */}
                    <Form.Item name="specific_event_triggered" valuePropName="checked">
                        <Checkbox>Was there a specific event that triggered your resignation?</Checkbox>
                    </Form.Item>

                    <Form.Item
                        noStyle
                        shouldUpdate={(prev, curr) => prev.specific_event_triggered !== curr.specific_event_triggered}
                    >
                        {({ getFieldValue }) =>
                            getFieldValue("specific_event_triggered") ? (
                                <Form.Item
                                    name="specific_event_details"
                                    label="Please describe the event"
                                    rules={[{ required: true, message: "Please provide details" }]}
                                >
                                    <TextArea rows={3} placeholder="e.g., Received a better offer, personal reasons, etc." />
                                </Form.Item>
                            ) : null
                        }
                    </Form.Item>

                    {/* Decision Timeline */}
                    <Form.Item
                        name="decision_timeline"
                        label="How long did you think about resigning before deciding?"
                        rules={[{ required: true, message: "Please select a timeline" }]}
                    >
                        <Radio.Group>
                            <Space direction="vertical">
                                {timelineOptions.map((opt) => (
                                    <Radio key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </Radio>
                                ))}
                            </Space>
                        </Radio.Group>
                    </Form.Item>

                    {/* Discussed with Supervisor */}
                    <Form.Item
                        name="discussed_with_supervisor"
                        label="Did you discuss your resignation with your supervisor before submitting?"
                        rules={[{ required: true }]}
                    >
                        <Radio.Group>
                            <Radio value={true}>Yes</Radio>
                            <Radio value={false}>No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Divider />

                    <Title level={4}>Rate Your Experience (1 = Poor, 5 = Excellent)</Title>

                    <Form.Item name="training_rating" label="Training Quality">
                        <Rate />
                    </Form.Item>
                    <Form.Item name="superior_relationship_rating" label="Relationship with Superior">
                        <Rate />
                    </Form.Item>
                    <Form.Item name="colleague_relationship_rating" label="Relationship with Colleagues">
                        <Rate />
                    </Form.Item>
                    <Form.Item name="salary_rating" label="Salary Satisfaction">
                        <Rate />
                    </Form.Item>
                    <Form.Item name="benefits_rating" label="Benefits Package">
                        <Rate />
                    </Form.Item>
                    <Form.Item name="benefits_comments" label="Comments on Benefits (optional)">
                        <TextArea rows={2} />
                    </Form.Item>
                    <Form.Item name="workload_rating" label="Workload Balance">
                        <Rate />
                    </Form.Item>
                    <Form.Item name="satisfaction_rating" label="Overall Job Satisfaction">
                        <Rate />
                    </Form.Item>

                    <Divider />

                    <Form.Item name="most_enjoyed" label="What did you enjoy most about working here?">
                        <TextArea rows={3} />
                    </Form.Item>

                    <Form.Item name="least_enjoyed" label="What did you enjoy least?">
                        <TextArea rows={3} />
                    </Form.Item>

                    <Form.Item name="encountered_problems" valuePropName="checked">
                        <Checkbox>Did you encounter any significant problems?</Checkbox>
                    </Form.Item>

                    <Form.Item
                        noStyle
                        shouldUpdate={(prev, curr) => prev.encountered_problems !== curr.encountered_problems}
                    >
                        {({ getFieldValue }) =>
                            getFieldValue("encountered_problems") ? (
                                <Form.Item name="problems_comment" label="Please describe the problems">
                                    <TextArea rows={3} />
                                </Form.Item>
                            ) : null
                        }
                    </Form.Item>

                    <Divider />

                    <Form.Item
                        name="recommend_employer"
                        label="Would you recommend this company as a good place to work?"
                    >
                        <Radio.Group>
                            <Radio value={true}>Yes</Radio>
                            <Radio value={false}>No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item name="recommend_comment" label="Any comments on recommendation? (optional)">
                        <TextArea rows={2} />
                    </Form.Item>

                    <Form.Item
                        name="reapply_future"
                        label="Would you consider reapplying to this company in the future?"
                    >
                        <Radio.Group>
                            <Radio value={true}>Yes</Radio>
                            <Radio value={false}>No</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item name="reapply_comment" label="Comments on reapplying (optional)">
                        <TextArea rows={2} />
                    </Form.Item>

                    <Form.Item name="additional_comments" label="Any additional comments?">
                        <TextArea rows={4} placeholder="Thank you for your feedback!" />
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" loading={submitting} size="large">
                            Submit Exit Interview
                        </Button>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
}